import React from 'react';
import { useNavigate } from 'react-router-dom';

import { isAuthError } from '../../api/client';
import { ADMIN_CHAT, DEVELOPER_CHAT } from '../../config/admin';
import { CLUB_INFO, CLUB_MAP_URL } from '../../config/club';
import { roleLabel } from '../../domain/auth/role';
import { countNotifications } from '../../domain/notifications/notifications';
import { getInitialsFromName, getUserDisplayName } from '../../domain/users/display';
import { RoleBadge } from '../../features/members/RoleBadge';
import { usersService } from '../../services/singletons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectMatchingEnabled } from '../../store/selectors/matchingSelectors';
import { selectNotificationPreferences } from '../../store/selectors/notificationSelectors';
import { authActions } from '../../store/slices/authSlice';
import { entitiesActions } from '../../store/slices/entitiesSlice';
import { uiActions } from '../../store/slices/uiSlice';
import {
  fetchMatchingStatus,
  fetchNotificationPreferences,
  updateMatchingStatus,
} from '../../store/thunks';
import { openTelegramLink } from '../../telegram/telegram';
import { TavernSwitch } from '../../ui/components';
import { ConfirmSheet } from '../../ui/ConfirmSheet';

import './ProfilePage.css';

export function ProfilePage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);
  const [pendingClubKey, setPendingClubKey] = React.useState<boolean | null>(null);
  const [isSavingClubKey, setIsSavingClubKey] = React.useState(false);

  const fullName = React.useMemo(() => {
    return getUserDisplayName(user);
  }, [user]);

  const onContactAdmin = () => {
    const url = ADMIN_CHAT.url.trim();
    if (!url) {
      dispatch(
        uiActions.setNotification({
          type: 'info',
          message: 'Контакт администратора пока не настроен.',
        }),
      );
      return;
    }

    openTelegramLink(url);
  };

  const onContactDeveloper = () => {
    const url = DEVELOPER_CHAT.url.trim();
    if (!url) {
      dispatch(
        uiActions.setNotification({
          type: 'info',
          message: 'Контакт разработчика пока не настроен.',
        }),
      );
      return;
    }

    openTelegramLink(url);
  };

  const confirmClubKeyChange = React.useCallback(async () => {
    if (pendingClubKey === null) return;
    const nextValue = pendingClubKey;
    setPendingClubKey(null);
    setIsSavingClubKey(true);
    try {
      const updatedUser = await usersService.updateCurrentUserClubKey({ hasClubKey: nextValue });
      dispatch(authActions.setHasClubKey(updatedUser.hasClubKey));
      dispatch(entitiesActions.upsertUser(updatedUser));
      dispatch(
        uiActions.setNotification({
          type: 'success',
          message: updatedUser.hasClubKey ? 'Наличие ключа отмечено' : 'Отметка о ключе снята',
        }),
      );
    } catch (error) {
      if (!isAuthError(error)) {
        const message =
          error instanceof Error ? error.message : 'Не удалось изменить отметку о ключе';
        dispatch(uiActions.setNotification({ type: 'error', message }));
      }
    } finally {
      setIsSavingClubKey(false);
    }
  }, [dispatch, pendingClubKey]);

  const isAdmin = user?.role === 'admin';
  const matchingEnabled = useAppSelector(selectMatchingEnabled);
  const [pendingMatching, setPendingMatching] = React.useState<boolean | null>(null);
  const [isSavingMatching, setIsSavingMatching] = React.useState(false);

  React.useEffect(() => {
    if (isAdmin) void dispatch(fetchMatchingStatus());
  }, [dispatch, isAdmin]);

  const notificationPreferences = useAppSelector(selectNotificationPreferences);
  const notificationCount = React.useMemo(
    () =>
      notificationPreferences ? countNotifications(notificationPreferences.categories) : null,
    [notificationPreferences],
  );

  const hasUser = user != null;
  React.useEffect(() => {
    if (hasUser) void dispatch(fetchNotificationPreferences());
  }, [dispatch, hasUser]);

  const confirmMatchingChange = React.useCallback(async () => {
    if (pendingMatching === null) return;
    const nextValue = pendingMatching;
    setPendingMatching(null);
    setIsSavingMatching(true);
    try {
      const enabled = await dispatch(updateMatchingStatus({ enabled: nextValue })).unwrap();
      dispatch(
        uiActions.setNotification({
          type: 'success',
          message: enabled ? 'Сбор игроков включён для всех' : 'Сбор игроков выключен для всех',
        }),
      );
    } catch (error) {
      if (!isAuthError(error)) {
        const message =
          error instanceof Error ? error.message : 'Не удалось изменить состояние сбора игроков';
        dispatch(uiActions.setNotification({ type: 'error', message }));
      }
    } finally {
      setIsSavingMatching(false);
    }
  }, [dispatch, pendingMatching]);

  return (
    <div className="ttbPage profile-page">
      <h1 className="ttbPageTitle profile-page-title">
        <span>👤</span>
        Профиль
      </h1>

      <div className="ttbPageContent">
        {!user ? (
          <div className="profile-empty-state">Нет данных пользователя</div>
        ) : (
          <div className="profile-content">
            <div className="profile-card">
              <div className="profile-avatar-wrap">
                <div className="profile-avatar-img">
                  {user.photo_url ? (
                    <img src={user.photo_url} alt={fullName} />
                  ) : (
                    <span className="profile-avatar-placeholder">
                      {getInitialsFromName(fullName)}
                    </span>
                  )}
                </div>
                <div className="profile-role-badge">
                  <RoleBadge role={user.role} />
                </div>
              </div>

              <h2 className="profile-user-name">{fullName}</h2>
              <div className="profile-user-role">{roleLabel(user.role)}</div>
            </div>

            {(user.role !== 'user' || user.hasClubKey) && (
              <div className="profile-key-card">
                <div className="profile-key-copy">
                  <div className="profile-key-title">Ключ от клуба</div>
                  <div className="profile-key-description">
                    Отметьте, если физический ключ находится у вас.
                  </div>
                </div>
                <TavernSwitch
                  checked={user.hasClubKey}
                  disabled={isSavingClubKey}
                  label={user.hasClubKey ? 'Ключ у меня' : 'Ключа нет'}
                  onChange={setPendingClubKey}
                />
              </div>
            )}

            {isAdmin && matchingEnabled !== null && (
              <div className="profile-key-card">
                <div className="profile-key-copy">
                  <div className="profile-key-title">Сбор игроков</div>
                  <div className="profile-key-description">
                    Поиск компании для всех пользователей приложения.
                  </div>
                </div>
                <TavernSwitch
                  checked={matchingEnabled}
                  disabled={isSavingMatching}
                  label={matchingEnabled ? 'Включён' : 'Выключен'}
                  onChange={setPendingMatching}
                />
              </div>
            )}

            <div className="profile-key-card">
              <div className="profile-key-copy">
                <div className="profile-key-title">🔔 Оповещения</div>
                <div className="profile-key-description">
                  {notificationCount
                    ? `Включено ${notificationCount.enabled} из ${notificationCount.total}. Выберите, о чём сообщать в Telegram.`
                    : 'Выберите, о чём сообщать в Telegram.'}
                </div>
              </div>
              <button
                type="button"
                className="button button-primary contact-button"
                onClick={() => navigate('/profile/notifications')}
              >
                Настроить
              </button>
            </div>

            {isAdmin && (
              <div className="profile-key-card">
                <div className="profile-key-copy">
                  <div className="profile-key-title">Гостевой доступ</div>
                  <div className="profile-key-description">
                    Открывайте столы, на которых гости смогут создавать мероприятия.
                  </div>
                </div>
                <button
                  type="button"
                  className="button button-primary contact-button"
                  onClick={() => navigate('/guests')}
                >
                  Управление гостевым доступом
                </button>
              </div>
            )}

            {/* Club Info Card */}
            <div className="club-info-card">
              <h3 className="club-info-title">
                <span>🏰</span>О Клубе &quot;{CLUB_INFO.name}&quot;
              </h3>

              <div className="info-item">
                <span className="info-icon">📍</span>
                <div className="info-content">
                  <div className="info-label">Адрес</div>
                  <div className="info-value">{CLUB_INFO.address}</div>
                  <a href={CLUB_MAP_URL} target="_blank" rel="noreferrer" className="map-link">
                    📍 Открыть на карте
                  </a>
                </div>
              </div>

              {CLUB_INFO.contacts.map((c) => (
                <div key={`${c.label}:${c.text}`} className="info-item">
                  <span className="info-icon">
                    {c.label.toLowerCase().includes('телефон') ||
                    c.label.toLowerCase().includes('phone')
                      ? '📞'
                      : '🌐'}
                  </span>
                  <div className="info-content">
                    <div className="info-label">{c.label}</div>
                    <div className="info-value">
                      {c.href ? (
                        <a
                          href={c.href}
                          target="_blank"
                          rel="noreferrer"
                          className="club-contact-link"
                        >
                          {c.text}
                        </a>
                      ) : (
                        c.text
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <div className="info-item">
                <span className="info-icon">🕐</span>
                <div className="info-content">
                  <div className="info-label">Часы работы</div>
                  <div className="info-value">{CLUB_INFO.hours}</div>
                </div>
              </div>

              <button className="button button-primary contact-button" onClick={onContactAdmin}>
                💬 Связаться с администрацией
              </button>

              <div className="developer-section">
                <p className="developer-hint">
                  Нашли баг или приложение работает некорректно? Сообщите разработчику:
                </p>
                <button
                  className="button contact-button contact-button--developer"
                  onClick={onContactDeveloper}
                >
                  🐛 Сообщить о проблеме
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmSheet
        open={pendingClubKey !== null}
        title="Изменить отметку о ключе?"
        message={
          pendingClubKey
            ? 'Подтвердите, что физический ключ от клуба находится у вас.'
            : 'Подтвердите, что физического ключа от клуба у вас больше нет.'
        }
        confirmLabel="Подтвердить"
        destructive={pendingClubKey === false}
        onCancel={() => setPendingClubKey(null)}
        onConfirm={() => {
          void confirmClubKeyChange();
        }}
      />

      <ConfirmSheet
        open={pendingMatching !== null}
        title={pendingMatching ? 'Включить сбор игроков?' : 'Выключить сбор игроков?'}
        message={
          pendingMatching
            ? 'Поиск компании снова станет доступен всем членам клуба.'
            : 'Новые заявки и подбор столов остановятся у всех. Уже собранные столы дойдут до конца, панель «Поиск игроков» будет скрыта.'
        }
        confirmLabel={pendingMatching ? 'Включить' : 'Выключить'}
        destructive={pendingMatching === false}
        onCancel={() => setPendingMatching(null)}
        onConfirm={() => {
          void confirmMatchingChange();
        }}
      />
    </div>
  );
}
